export interface LegendItem {
  color: string;
  title: string;
  order?: number;
}

export interface LegendData {
  resource: LegendItem[];
}

import { pgSelect } from './postgrest';

const themeToTableMap: Record<string, string> = {
  soil_zone_sld: 'sk_soil_zone',
  assoctheme: 'prt_mapunit_view',
  dom_ag_cap_sld: 'capclass',
  dominant_texture_style: 'text_colours',
  salsym_style_css: 'salinity_affect_prod',
  phsym_style_css: 'phelegend_new',
  initilal_irrig_pooled: 'initilal_irrig_pooled',
};

function sortByOrder(data: LegendData | null): LegendData | null {
  if (!data) return data;
  return {
    ...data,
    resource: [...data.resource].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
  };
}

const mockLegendData: Record<string, LegendData> = {
  soil_zone_sld: {
    resource: [
      { color: '8B776B', title: 'Brown', order: 1 },
      { color: '6F5F4C', title: 'Dark Brown', order: 2 },
      { color: '525251', title: 'Black', order: 3 },
      { color: '413A34', title: 'Thick Black', order: 4 },
      { color: '656260', title: 'Dark Grey', order: 5 },
      { color: 'CBCCCD', title: 'Grey', order: 6 },
      { color: 'B2DF8A', title: 'Organic', order: 7 },
    ],
  },
  dom_ag_cap_sld: {
    resource: [
      { color: 'BCD4E6', title: 'Class O', order: 1 },
      { color: 'A0522D', title: 'Class 1', order: 2 },
      { color: 'B8860B', title: 'Class 2', order: 3 },
      { color: 'EAA221', title: 'Class 3', order: 4 },
      { color: 'F8DE7E', title: 'Class 4', order: 5 },
      { color: 'C9DC89', title: 'Class 5', order: 6 },
      { color: 'BDDA57', title: 'Class 6', order: 7 },
      { color: 'E68FAC', title: 'Class 7', order: 8 },
      { color: 'F5F5F5', title: 'Unclassified', order: 9 },
    ],
  },
  dominant_texture_style: {
    resource: [
      { color: 'FFFACD', title: 'Coarse', order: 7 },
      { color: 'f7c273', title: 'Moderately Coarse', order: 12 },
      { color: '87CEEB', title: 'Medium', order: 16 },
      { color: 'FF8C00', title: 'Moderately Fine', order: 19 },
      { color: 'DB7093', title: 'Fine', order: 22 },
      { color: 'ded560', title: 'Organic', order: 23 },
      { color: 'F5F5F5', title: 'Unclassified', order: 0 },
    ],
  },
  salsym_style_css: {
    resource: [
      { color: 'C2FFE9', title: 'None', order: 1 },
      { color: '4682B4', title: 'Very slight', order: 2 },
      { color: '483D8B', title: 'Slight', order: 3 },
      { color: 'B8B8FF', title: 'Moderate', order: 4 },
      { color: 'FFB6C1', title: 'Severe', order: 5 },
    ],
  },
  phsym_style_css: {
    resource: [
      { color: 'e31a1c', title: 'Dominantly acid soils', order: 1 },
      { color: 'fafa00', title: 'Some acid soils', order: 2 },
      { color: '33a02c', title: 'Neutral soils', order: 3 },
      { color: 'f6a103', title: 'Some alkaline soils', order: 4 },
      { color: '9a5832', title: 'Dominantly alkaline soils', order: 5 },
    ],
  },
  initilal_irrig_pooled: {
    resource: [
      { color: '238b45', title: 'Excellent', order: 1 },
      { color: '78c679', title: 'Good', order: 2 },
      { color: 'ffffcc', title: 'Fair', order: 3 },
      { color: 'fe9929', title: 'Poor', order: 4 },
    ],
  },
};

export const legendService = {
  async getColoursForTheme(style: string): Promise<LegendData | null> {
    const theme = themeToTableMap[style];

    if (!theme) {
      return null;
    }

    try {
      const data = await pgSelect(theme, '', { cache: 'no-cache' });

      return sortByOrder(data);
    } catch (error) {
      console.error('Error fetching legend data:', error);
      console.log('Falling back to mock data for testing purposes');

      return sortByOrder(mockLegendData[style] || null);
    }
  },
};
